import React, { useState } from "react";
import avatar from "../../../assets/images/front/profile.webp";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { communicationList } from "../../../services/communicationService"; // adjust path
import { toast } from "react-toastify";

// Format sentAt date into readable string
const formatMessageDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: "short" });
  } else if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  } else {
    return date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
};

const MessageList = ({ filters = {}, search = "", onSelectChat }) => {
  const [activeMessageId, setActiveMessageId] = useState(null);
  const { session } = useSelector((state) => state?.authProvider);

  const currentUserId = session?.user?._id;
  // organizationId may not always be present in session
  const organizationId = session?.user?.organization;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["communication-list", filters, search],
    queryFn: async () => {
      const res = await communicationList({
        ...filters,
        ...(search && { keyword: search }),
        // ...(organizationId && { organization: organizationId }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs?.data ?? [];
    },
    enabled: true, // adjust if you need to wait for session
  });

  // Given a conversation item, find the "other" member (not current user)
  const getOtherMember = (item) => {
    if (!item?.memberData?.length) return null;
    // If only one member, just show that one
    if (item.memberData.length === 1) return item.memberData[0];
    return (
      item.memberData.find((m) => m._id !== currentUserId) ?? item.memberData[0]
    );
  };

  const getDisplayName = (member) => {
    if (!member) return "Unknown";
    return `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim();
  };

  const getAvatarUrl = (member) => {
    return member?.image?.url || null;
  };

  const getMessagePreview = (item) => {
    if (!item?.latestMessage?.message) return "";
    const sender = item.memberData?.find(
      (m) => m._id === item.latestMessage.sentBy,
    );
    const senderName = sender ? sender.firstName : "";
    const isSentByMe = item.latestMessage.sentBy === currentUserId;
    const prefix = isSentByMe ? "You" : senderName;
    return prefix
      ? `${prefix}: ${item.latestMessage.message}`
      : item.latestMessage.message;
  };

  if (isLoading || isFetching) {
    return (
      <div className="w-full bg-white max-h-[calc(100vh-320px)] overflow-auto">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="flex items-center space-x-3 px-3 py-4 border-b border-b-gray-200 animate-pulse"
          >
            <div className="rounded-full w-12 h-12 bg-gray-200 shrink-0" />
            <div className="flex flex-col gap-2 w-full">
              <div className="h-3.5 bg-gray-200 rounded w-1/3" />
              <div className="h-3 bg-gray-100 rounded w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="w-full bg-white flex items-center justify-center py-16 text-sm text-gray-400">
        No conversations found.
      </div>
    );
  }

  return (
    <div className="w-full bg-white max-h-[calc(100vh-320px)] overflow-auto">
      {data.map((item) => {
        const otherMember = getOtherMember(item);
        const displayName = getDisplayName(otherMember);
        const avatarUrl = getAvatarUrl(otherMember);
        const messagePreview = getMessagePreview(item);
        const date = formatMessageDate(item.latestMessage?.sentAt);

        // Unread: check if current user's _id is NOT in seenBy
        const seenBy = item.communicationData?.seenBy ?? [];
        const isUnread = !seenBy.some((s) => s.user === currentUserId);

        return (
          <div
            key={item._id}
            onClick={() => {
              setActiveMessageId(item._id);
              onSelectChat(item); // 👈 important
            }}
            className={`flex items-center justify-between px-3 py-4 border-b border-b-gray-200 cursor-pointer group border-l-4 hover:bg-gray-100 ${
              activeMessageId === item._id
                ? "bg-slate-100 border-emerald-800"
                : "border-transparent"
            }`}
          >
            <div className="flex items-center space-x-3 w-full">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="rounded-full w-12 h-12 object-cover shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-palatte flex items-center justify-center text-white font-semibold text-lg shrink-0">
                  {displayName?.charAt(0)?.toUpperCase() || "U"}
                </div>
              )}
              <div className="flex flex-col w-full min-w-0">
                <div className="flex gap-2 items-start justify-between">
                  <p
                    className={`text-gray-700 text-base truncate ${
                      isUnread ? "font-semibold" : "font-medium"
                    }`}
                  >
                    {displayName}
                  </p>
                  <span className="text-xs text-gray-800 pt-1 shrink-0 group-hover:hidden">
                    {date}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <p
                    className={`text-xs truncate ${
                      isUnread ? "text-gray-800 font-medium" : "text-gray-600"
                    }`}
                  >
                    {messagePreview}
                  </p>
                  {/* {isUnread && (
                    <span className="ml-auto shrink-0 w-2 h-2 rounded-full bg-emerald-600" />
                  )} */}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MessageList;
